import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  createTheme,
  CssBaseline,
  ThemeProvider,
  Typography,
} from "@mui/material";

const theme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#060707",
    },
  },
  typography: {
    fontFamily:
      "SF Pro Text, system-ui, -apple-system, BlinkMacSystemFont, Helvetica Neue, Helvetica, Arial, sans-serif",
  },
});

const appMeta = {
  "Home Assistant": { detail: "Slim huis", accent: "#31d7c4" },
  Plex: { detail: "Mediaserver", accent: "#f2b84b" },
  "Google Maps": { detail: "Navigatie", accent: "#57c45b" },
  Waze: { detail: "Verkeer", accent: "#55c9ff" },
  ABRP: { detail: "Routeplanner", accent: "#ff6b5f" },
  "Delta TV": { detail: "Live tv", accent: "#2bc5f4" },
  "F1 TV": { detail: "Formula 1", accent: "#ff2a35" },
};

const getFullscreenUrl = (url) => {
  const youtubeUrl = "https://www.youtube.com/redirect?q=";
  return youtubeUrl + encodeURIComponent(url);
};

const checkFileExists = async (url) => {
  try {
    const response = await fetch(url, { method: "HEAD" });
    return response.ok;
  } catch (error) {
    return false;
  }
};

const getGreeting = (date) => {
  const hour = date.getHours();

  if (hour < 6) return "Goedenacht";
  if (hour < 12) return "Goedemorgen";
  if (hour < 18) return "Goedemiddag";
  return "Goedenavond";
};

const defaultLocation = {
  latitude: 52.1326,
  longitude: 5.2913,
};

const weatherCodeMap = {
  0: { label: "Helder", icon: "clear" },
  1: { label: "Overwegend helder", icon: "partly" },
  2: { label: "Half bewolkt", icon: "partly" },
  3: { label: "Bewolkt", icon: "cloud" },
  45: { label: "Mist", icon: "fog" },
  48: { label: "Rijpmist", icon: "fog" },
  51: { label: "Lichte motregen", icon: "rain" },
  53: { label: "Motregen", icon: "rain" },
  55: { label: "Zware motregen", icon: "rain" },
  56: { label: "IJzel", icon: "rain" },
  57: { label: "Zware ijzel", icon: "rain" },
  61: { label: "Lichte regen", icon: "rain" },
  63: { label: "Regen", icon: "rain" },
  65: { label: "Zware regen", icon: "rain" },
  66: { label: "IJzel", icon: "rain" },
  67: { label: "Zware ijzel", icon: "rain" },
  71: { label: "Lichte sneeuw", icon: "snow" },
  73: { label: "Sneeuw", icon: "snow" },
  75: { label: "Zware sneeuw", icon: "snow" },
  77: { label: "Sneeuwkorrels", icon: "snow" },
  80: { label: "Lichte buien", icon: "rain" },
  81: { label: "Buien", icon: "rain" },
  82: { label: "Zware buien", icon: "rain" },
  85: { label: "Sneeuwbuien", icon: "snow" },
  86: { label: "Zware sneeuwbuien", icon: "snow" },
  95: { label: "Onweer", icon: "storm" },
  96: { label: "Onweer met hagel", icon: "storm" },
  99: { label: "Zwaar onweer", icon: "storm" },
};

const getWeatherInfo = (code, isDay) => {
  const info = weatherCodeMap[code] || { label: "Weer", icon: "cloud" };

  if (info.icon === "clear" && !isDay) {
    return { ...info, icon: "moon" };
  }

  return info;
};

const resolveWeatherLocation = async () => {
  try {
    const response = await fetch("https://ipapi.co/json/");
    if (!response.ok) throw new Error("IP-locatie niet beschikbaar");
    const data = await response.json();
    const latitude = Number(data.latitude);
    const longitude = Number(data.longitude);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { latitude, longitude };
    }
  } catch (error) {
    return defaultLocation;
  }

  return defaultLocation;
};

const WeatherIcon = ({ code, isDay }) => {
  const { icon } = getWeatherInfo(code, isDay);
  const stroke = "#f5f7fb";
  const sun = "#f8b84e";
  const cloud = "#aeb8c8";
  const rain = "#57b7ff";

  return (
    <Box
      component="svg"
      viewBox="0 0 64 64"
      aria-hidden
      sx={{ width: { xs: 28, md: 36 }, height: { xs: 28, md: 36 }, flex: "0 0 auto" }}
    >
      {(icon === "clear" || icon === "partly") && (
        <>
          <circle cx="24" cy="24" r="10" fill={sun} />
          <g stroke={sun} strokeWidth="4" strokeLinecap="round">
            <path d="M24 4v7" />
            <path d="M24 37v7" />
            <path d="M4 24h7" />
            <path d="M37 24h7" />
            <path d="m10 10 5 5" />
            <path d="m33 33 5 5" />
            <path d="m38 10-5 5" />
            <path d="m15 33-5 5" />
          </g>
        </>
      )}
      {icon === "moon" && (
        <path
          d="M43 39c-11 4-22-4-22-16 0-6 3-11 7-15-12 2-20 12-20 24 0 14 11 24 24 24 10 0 19-6 23-15-4 2-8 2-12 0Z"
          fill="#8ba6ff"
        />
      )}
      {(icon === "cloud" || icon === "partly" || icon === "rain" || icon === "snow" || icon === "storm" || icon === "fog") && (
        <path
          d="M22 45h25c7 0 12-5 12-12s-5-12-12-12c-3 0-6 1-8 3-3-7-9-11-17-11-10 0-18 8-18 18 0 8 6 14 18 14Z"
          fill={cloud}
          opacity="0.95"
        />
      )}
      {icon === "rain" && (
        <g stroke={rain} strokeWidth="4" strokeLinecap="round">
          <path d="M23 50 19 58" />
          <path d="M35 50 31 58" />
          <path d="M47 50 43 58" />
        </g>
      )}
      {icon === "snow" && (
        <g stroke="#d9f0ff" strokeWidth="3" strokeLinecap="round">
          <path d="M24 52v8" />
          <path d="M20 56h8" />
          <path d="M40 52v8" />
          <path d="M36 56h8" />
        </g>
      )}
      {icon === "storm" && (
        <path d="M32 45 23 62l17-14h-8l7-12-15 13h8Z" fill="#ffd54a" />
      )}
      {icon === "fog" && (
        <g stroke={stroke} strokeWidth="3" strokeLinecap="round" opacity="0.75">
          <path d="M13 50h38" />
          <path d="M19 57h31" />
        </g>
      )}
    </Box>
  );
};

function App() {
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [bookmarks, setBookmarks] = useState([]);
  const [heroImage, setHeroImage] = useState(null);
  const [weather, setWeather] = useState({
    code: 3,
    isDay: true,
    label: "Weer laden",
    temperature: null,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const fileExists = await checkFileExists("/bookmarks.json");
        if (!fileExists) {
          setError("Bestand niet gevonden");
          return;
        }

        const response = await fetch("/bookmarks.json");
        if (!response.ok) {
          throw new Error("Fout bij het ophalen van bookmarks");
        }

        const data = await response.json();
        setBookmarks(data.categories || []);
        setHeroImage(data.heroImage || null);
        setError(null);
      } catch (fetchError) {
        setError(fetchError.message || "Er is een onbekende fout opgetreden");
      }
    };

    fetchBookmarks();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = "Tesla Dashboard";
  }, []);

  useEffect(() => {
    let active = true;

    const fetchWeather = async () => {
      try {
        const location = await resolveWeatherLocation();
        const params = new URLSearchParams({
          latitude: location.latitude.toFixed(4),
          longitude: location.longitude.toFixed(4),
          current: "temperature_2m,weather_code,is_day",
          timezone: "auto",
        });
        const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);

        if (!response.ok) {
          throw new Error("Weerdata niet beschikbaar");
        }

        const data = await response.json();
        const current = data.current || {};
        const code = Number(current.weather_code);
        const isDay = Number(current.is_day) === 1;
        const weatherInfo = getWeatherInfo(code, isDay);

        if (active) {
          setWeather({
            code,
            isDay,
            label: weatherInfo.label,
            temperature: Math.round(Number(current.temperature_2m)),
          });
        }
      } catch (error) {
        if (active) {
          setWeather((previousWeather) => ({
            ...previousWeather,
            label: "Weer onbekend",
          }));
        }
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const allBookmarks = useMemo(
    () => bookmarks.flatMap((category) => category.bookmarks || []),
    [bookmarks]
  );

  const greeting = getGreeting(currentDateTime);

  const formattedDate = currentDateTime.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const formattedTime = currentDateTime.toLocaleTimeString("nl-NL", {
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          height: "100vh",
          color: "#f7f8f8",
          display: "block",
          overflow: "hidden",
          px: { xs: 2, md: 7, lg: 9 },
          py: { xs: 2, md: 5.5, lg: 6 },
          background:
            "radial-gradient(circle at 50% -10%, rgba(228,38,38,0.18), transparent 34%), linear-gradient(180deg, #151719 0%, #08090a 48%, #010101 100%)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            minHeight: 0,
            background: "transparent",
            overflow: "hidden",
            display: "grid",
            gridTemplateRows: { xs: "68px 136px 1fr", md: "92px 190px minmax(0, 1fr)" },
            p: 0,
            gap: { xs: 1.2, md: 2.75 },
          }}
        >
          <Box
            component="header"
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              px: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
              <Typography
                aria-hidden
                sx={{
                  color: "#e82127",
                  fontSize: { xs: "2rem", md: "2.7rem" },
                  fontWeight: 900,
                  lineHeight: 1,
                  textShadow: "0 0 24px rgba(232,33,39,0.55)",
                }}
              >
                T
              </Typography>
              <Box>
                <Typography sx={{ fontSize: { xs: "1.05rem", md: "1.35rem" }, fontWeight: 800 }}>
                  Tesla Dashboard
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: { xs: "1.65rem", md: "2.35rem" }, fontWeight: 700, lineHeight: 1 }}>
                {formattedTime}
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  color: "rgba(255,255,255,0.5)",
                  fontSize: { xs: "0.78rem", md: "0.95rem" },
                  textTransform: "capitalize",
                }}
              >
                {formattedDate}
              </Typography>
            </Box>

            <Box
              sx={{
                justifySelf: "end",
                display: "flex",
                alignItems: "center",
                gap: 1.1,
              }}
            >
              <WeatherIcon code={weather.code} isDay={weather.isDay} />
              <Box>
                <Typography sx={{ fontSize: { xs: "0.95rem", md: "1.15rem" }, fontWeight: 800 }}>
                  {weather.temperature === null ? "--" : weather.temperature}°C
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: { xs: "0.78rem", md: "0.95rem" } }}>
                  {weather.label}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.1)",
              backgroundImage: heroImage
                ? `linear-gradient(90deg, rgba(7,10,15,0.92) 0%, rgba(8,10,15,0.78) 34%, rgba(8,10,15,0.18) 64%, rgba(8,10,15,0.08) 100%), linear-gradient(180deg, rgba(4,5,8,0.08), rgba(4,5,8,0.5)), url("${heroImage}")`
                : "linear-gradient(90deg, rgba(13,18,26,0.94) 0%, rgba(22,20,28,0.8) 42%, rgba(57,17,19,0.62) 100%)",
              backgroundPosition: "center center",
              backgroundSize: "cover",
              backgroundColor: "#10141d",
              position: "relative",
              overflow: "hidden",
              px: { xs: 2, md: 5 },
              display: "flex",
              alignItems: "center",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "2px",
                background: "#e82127",
                boxShadow: "0 0 24px rgba(232,33,39,0.85)",
                margin: { xs: "28px 0 28px 20px", md: "42px 0 42px 30px" },
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1, ml: { xs: 2.4, md: 3.8 } }}>
              <Typography sx={{ fontSize: { xs: "1.3rem", md: "2.15rem" }, fontWeight: 700 }}>
                {greeting}
              </Typography>
              <Typography sx={{ mt: 0.9, color: "rgba(255,255,255,0.66)", fontSize: { xs: "0.92rem", md: "1.15rem" } }}>
                Alles wat je nodig hebt.
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.66)", fontSize: { xs: "0.92rem", md: "1.15rem" } }}>
                Op een plek.
              </Typography>
            </Box>
          </Box>

          <Box component="main" sx={{ minHeight: 0 }}>
            {error ? (
              <Box
                sx={{
                  height: "100%",
                  p: 3,
                  border: "1px solid rgba(255,107,95,0.45)",
                  borderRadius: "8px",
                  background: "rgba(255,107,95,0.1)",
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>Controleer bookmarks.json</Typography>
                <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.72)" }}>
                  {error}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gridTemplateRows: "repeat(3, minmax(0, 1fr))",
                  gap: { xs: 1.1, md: 2.2 },
                  height: "100%",
                }}
              >
                {allBookmarks.map((bookmark, index) => {
                  const meta = appMeta[bookmark.name] || {
                    detail: "Snelkoppeling",
                    accent: "#e82127",
                  };

                  return (
                    <Box
                      key={`${bookmark.name}-${index}`}
                      component="a"
                      href={getFullscreenUrl(bookmark.url)}
                      target="_self"
                      sx={{
                        minHeight: 0,
                        px: { xs: 1.3, md: 3.2 },
                        py: { xs: 1.2, md: 2.6 },
                        display: "grid",
                        gridTemplateColumns: { xs: "62px 1fr", md: "94px 1fr" },
                        alignItems: "center",
                        gap: { xs: 1.2, md: 2.4 },
                        color: "#f7f8f8",
                        textDecoration: "none",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,255,255,0.09)",
                        background:
                          "linear-gradient(135deg, rgba(35,45,53,0.92), rgba(17,22,27,0.96))",
                        boxShadow:
                          "0 16px 30px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.06)",
                        transition:
                          "transform 160ms ease, border-color 160ms ease, background 160ms ease",
                        overflow: "hidden",
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          inset: 0,
                          background: `radial-gradient(circle at 17% 50%, ${meta.accent}25, transparent 32%)`,
                          opacity: 0.9,
                        },
                        "&:hover": {
                          transform: "translateY(-2px)",
                          borderColor: "rgba(255,255,255,0.22)",
                          background:
                            "linear-gradient(135deg, rgba(43,55,64,0.96), rgba(21,27,32,0.98))",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={bookmark.image}
                        alt={bookmark.name}
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          width: { xs: 50, md: 72 },
                          height: { xs: 50, md: 72 },
                          p: { xs: 0.55, md: 0.8 },
                          objectFit: "contain",
                          borderRadius: "8px",
                          background: bookmark.background || "#f7f8f8",
                          border: bookmark.border || "none",
                          boxShadow: `0 0 0 1px rgba(255,255,255,0.14), 0 0 24px ${meta.accent}33`,
                        }}
                      />

                      <Box sx={{ position: "relative", zIndex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.94)",
                            fontSize: { xs: "0.86rem", md: "1.32rem" },
                            fontWeight: 700,
                            lineHeight: 1.1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {bookmark.name}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.45,
                            color: "rgba(255,255,255,0.46)",
                            fontSize: { xs: "0.68rem", md: "0.98rem" },
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {meta.detail}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
